import { defineStore } from 'pinia';
import { THEME_SYSTEM } from '@/constants/theme';

/**
 * User store - replaces window.User
 * Manages current authenticated user state
 */
export const useUserStore = defineStore('user', {
    state: () => ({
        id: 0,
        salutation: '',
        firstname: '',
        lastname: '',
        email: '',
        mobile: '',
        picture: '',
        language: 'en',
        timezone: 'UTC',
        theme: THEME_SYSTEM,
        organizations: [],
        verified: true,
        google2fa: 0,
        receive_marketing: 0,
        receive_notification: 0,
        platform_role: null,
        has_password: true,
        pending_email: null
    }),

    getters: {
        /**
         * Get user's full name
         */
        fullName: state => {
            return `${state.firstname} ${state.lastname}`.trim();
        },

        /**
         * Get user's display name (salutation + name)
         */
        displayName: state => {
            const parts = [];
            if (state.salutation) parts.push(state.salutation);
            if (state.firstname) parts.push(state.firstname);
            if (state.lastname) parts.push(state.lastname);
            return parts.join(' ');
        },

        /**
         * Check if user is authenticated
         */
        isAuthenticated: state => {
            return state.id > 0;
        },

        /**
         * Check if user has 2FA enabled
         */
        has2FA: state => {
            return state.google2fa === 1;
        },

        /**
         * Get user's organizations
         */
        orgs: state => {
            return state.organizations;
        },

        isPlatformManager: state => {
            return state.platform_role === 'manager';
        },

        isPlatformViewer: state => {
            return state.platform_role === 'viewer';
        },

        hasPlatformRole: state => {
            return state.platform_role !== null;
        }
    },

    actions: {
        /**
         * Initialize store from window.User (for backward compatibility)
         */
        initFromLegacy() {
            if (typeof window !== 'undefined' && window.User) {
                this.setUser(window.User);
            }
        },

        /**
         * Set user data
         * @param {Object} userData - User data object
         */
        setUser(userData) {
            this.id = userData.id || 0;
            this.salutation = userData.salutation || '';
            this.firstname = userData.firstname || '';
            this.lastname = userData.lastname || '';
            this.email = userData.email || '';
            this.mobile = userData.mobile || '';
            this.picture = userData.picture || '';
            this.language = userData.language || 'en';
            this.timezone = userData.timezone || 'UTC';
            this.theme = userData.theme || THEME_SYSTEM;
            this.organizations = userData.organizations || [];
            this.verified = userData.verified ?? true;
            this.google2fa = userData.google2fa || 0;
            this.receive_marketing = userData.receive_marketing || 0;
            this.receive_notification = userData.receive_notification || 0;
            this.platform_role = userData.platform_role || null;
            this.has_password = userData.has_password ?? true;
            this.pending_email = userData.pending_email || null;
        },

        /**
         * Load user data from API
         */
        async load() {
            try {
                const response = await axios.get('/api/user');
                this.setUser(response.data);
            } catch (error) {
                console.error('Failed to load user data:', error);
            }
        },

        /**
         * Update user profile
         * @param {Object} data - Profile data to update
         */
        async updateProfile(data) {
            try {
                const response = await axios.put('/api/user/profile', data);
                this.setUser(response.data);
                return response.data;
            } catch (error) {
                throw error;
            }
        },

        /**
         * Clear user data (logout)
         */
        clear() {
            this.id = 0;
            this.salutation = '';
            this.firstname = '';
            this.lastname = '';
            this.email = '';
            this.mobile = '';
            this.picture = '';
            this.language = 'en';
            this.timezone = 'UTC';
            this.theme = THEME_SYSTEM;
            this.organizations = [];
            this.verified = true;
            this.google2fa = 0;
            this.receive_marketing = 0;
            this.receive_notification = 0;
            this.platform_role = null;
            this.has_password = true;
            this.pending_email = null;
        }
    }
});
